import { globalStyles } from '@ctw/shared/content/document-viewer/_internal/renderers/pdf/pdf-cda-renderer';
import {
  filterChildNodesByNodeName,
  isParsedListElement,
  uniqueIdentifierForNode,
} from '@ctw/shared/utils/cda/helpers';
import type { CdaParsedHtmlElementNode } from '@ctw/shared/utils/cda/types';
import { toRoman } from '@ctw/shared/utils/roman-numerals';
import { StyleSheet, Text, View } from '@react-pdf/renderer';
import { memo, useMemo } from 'react';
import { PdfNodeRenderer } from './pdf-node-renderer';

interface PdfListRendererProps {
  id: string;
  list: CdaParsedHtmlElementNode;
  nestingLevel?: number;
}

const listStyles = StyleSheet.create({
  listItem: {
    fontWeight: 400,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  bullet: {
    fontWeight: 400,
    marginRight: 5,
    fontSize: 12,
    lineHeight: 1.5,
  },
});

export const PdfListRenderer = memo(({ list, id, nestingLevel = 0 }: PdfListRendererProps) => {
  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentionally under-specific deps
  const listType = useMemo(
    () =>
      list.attributes.listType === 'ordered' || list.nodeName === 'ol' ? 'ordered' : 'unordered',
    [id],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentionally under-specific deps
  const children = useMemo(
    () => filterChildNodesByNodeName(list, ['item', 'li', 'ul', 'ol', 'list'], false),
    [id],
  );

  const enumerator = (listType: 'ordered' | 'unordered', index: number) => {
    if (listType === 'ordered') {
      const style = nestingLevel % 3;
      let enumeration: string;

      switch (style) {
        case 0:
          enumeration = `${index + 1}.`;
          break;
        case 1: {
          // Supports up to 702 entries : a-z, aa-zz
          const second = String.fromCharCode(97 + (index % 26));
          let first = '';
          if (index / 26 > 1) {
            first = String.fromCharCode(97 + (Math.floor(index / 26) % 26));
          }
          enumeration = `${first}${second}.`;
          break;
        }
        default:
          enumeration = `${toRoman(index + 1).toLowerCase()}.`; // i, ii, iii...
          break;
      }
      return (
        <Text style={[listStyles.bullet, nestingLevel >= 10 ? { marginLeft: 0 } : {}]}>
          {enumeration}
        </Text>
      );
    }
    const bullets = ['•', '-']; // Note: Can only use characters supported by font
    const bullet = bullets[nestingLevel % bullets.length];
    return (
      <Text style={[listStyles.bullet, nestingLevel >= 10 ? { marginLeft: 0 } : {}]}>{bullet}</Text>
    );
  };

  const nestedListRenderer = (node: CdaParsedHtmlElementNode, index: number) => {
    const nestedListIdentifier = uniqueIdentifierForNode({
      node,
      context: `${id}-nested-${nestingLevel}`,
      purpose: 'semantic-list-item',
      index,
    });

    return (
      <PdfListRenderer
        list={node}
        id={nestedListIdentifier}
        key={nestedListIdentifier}
        nestingLevel={nestingLevel + 1}
      />
    );
  };

  return (
    <View style={globalStyles.container}>
      {children.map((child, index) => {
        if (isParsedListElement(child)) {
          return nestedListRenderer(child, index);
        }

        const listItemIdentifier = uniqueIdentifierForNode({
          node: child,
          context: id,
          purpose: 'semantic-list-item',
          index,
        });

        if (child.childNodes.length > 1) {
          child.childNodes = [
            {
              nodeType: 'element',
              nodeName: 'div',
              childNodes: child.childNodes,
              isEffectivelyEmpty: child.childNodes.every((child) => child.isEffectivelyEmpty),
              attributes: {},
              styles: {
                pdfStyles: {},
                className: '',
                extensions: [],
              },
            },
          ];
        }

        if (child.childNodes[0]) {
          return (
            <View id={listItemIdentifier} key={listItemIdentifier} style={listStyles.listItem}>
              {enumerator(listType, index)}
              <View style={{ flex: 1 }}>
                <PdfNodeRenderer
                  id={listItemIdentifier}
                  node={child.childNodes[0]}
                  indentLevel={nestingLevel + 1}
                />
              </View>
            </View>
          );
        }

        return undefined;
      })}
    </View>
  );
});
